<script setup lang="ts">
import { onMounted, computed, ref, watch, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import { RocketLaunchIcon, StarIcon, TrophyIcon, AcademicCapIcon, SparklesIcon, ArrowPathIcon, CheckIcon } from '@heroicons/vue/24/solid'
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, ChevronDownIcon, UserGroupIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import { useVariableStore, type Variable } from '@/stores/useVariableStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useBoostCoreUsersStore } from '@/stores/useBoostCoreUsersStore'
import { useMyTeamStore } from '@/stores/useMyTeamStore'
import { storeToRefs } from 'pinia'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import IconWithTooltip from '@/components/common/ui/IconWithTooltip.vue'
import Tooltip from '@/components/common/ui/Tooltip.vue'
import VariableGridActions from '@/components/features/variable/VariableGridActions.vue'
import VariableEdit from '@/components/features/variable/VariableEdit.vue'
import PalierProgress from '@/components/features/variable/PalierProgress.vue'
import TeamPercentProgress from '@/components/features/variable/TeamPercentProgress.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'

const route = useRoute()
const router = useRouter()
let titleClickCount = 0
let titleClickTimer: ReturnType<typeof setTimeout> | null = null
function onTitleClick() {
  titleClickCount++
  if (titleClickTimer) clearTimeout(titleClickTimer)
  titleClickTimer = setTimeout(() => { titleClickCount = 0 }, 500)
  if (titleClickCount >= 3 && isSuperAdmin.value) router.push('/variables/lucca-admin')
}
const variableStore = useVariableStore()
const boostCoreStore = useBoostCorePermissionStore()
const usersStore = useBoostCoreUsersStore()
const myTeamStore = useMyTeamStore()
const { isManager: isManagerConnected } = storeToRefs(boostCoreStore)

const isSuperAdmin = computed(() => boostCoreStore.include('SUPER_ADMIN'))
const isSalesManager = computed(() => boostCoreStore.include('SALES_MANAGER'))
const isSalesDirector = computed(() => boostCoreStore.include('SALES_DIRECTOR'))
const isDG = computed(() => boostCoreStore.include('DG') || isSuperAdmin.value)
const canSelectUser = computed(() => isSuperAdmin.value || isSalesManager.value || isSalesDirector.value)
const canEditAdjustments = computed(() => isDG.value)
const canValidateForTeam = computed(() => isSalesManager.value || isSalesDirector.value || isSuperAdmin.value)

const isManager = computed(() => {
  if (!currentVariable.value) return isManagerConnected.value
  const nbEtp = currentVariable.value?.salesEngineer?.metadata?.nbEtp || 0
  const nbEtpNewbie = currentVariable.value?.salesEngineer?.metadata?.nbEtpNewbie ?? undefined
  return nbEtp > 0 || (nbEtpNewbie != null && nbEtpNewbie > 0)
})

const displayUsers = computed(() =>
  isSuperAdmin.value ? usersStore.users : myTeamStore.all,
)
const userOptions = computed(() => {
  const active = displayUsers.value.filter((u) => u.status === 'ACTIVE')
  const opts = active.map((u) => ({
    id: u.uuid,
    value: u.uuid,
    label: usersStore.getUserLabel(u),
  }))
  if (boostCoreStore.user && !opts.some((o) => o.value === boostCoreStore.user?.uuid)) {
    opts.unshift({
      id: boostCoreStore.user.uuid,
      value: boostCoreStore.user.uuid,
      label: usersStore.getUserLabel(boostCoreStore.user as any),
    })
  }
  return opts
})
const { variables, currentVariable, dateSelected } = storeToRefs(variableStore)
const variablesForYear = ref<Variable[]>([])
const monthDropdownOpen = ref(false)
const pendingValidation = ref<any[]>([])

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']

type MonthStatus = 'avenir' | 'en_cours' | 'a_valider' | 'valide_ic' | 'valide_manager' | 'envoye_rh'

function getMonthStatus(year: number, month: number, variable: Variable | null): MonthStatus {
  const now = new Date()
  const isFuture = year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth())
  if (isFuture) return 'avenir'
  if (!variable) return year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth()) ? 'a_valider' : 'en_cours'
  const status = variable.variableStatus
  if (status === 'SENT_TO_LUCCA') return 'envoye_rh'
  if (status === 'VALIDATION_MANAGER') return 'valide_manager'
  if (status === 'VALIDATION_IC') return 'valide_ic'
  if (status === 'INIT') {
    const isPastMonth = year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth())
    return isPastMonth ? 'a_valider' : 'en_cours'
  }
  return 'en_cours'
}

function getStatusColor(status: MonthStatus): string {
  switch (status) {
    case 'avenir': return 'bg-gray-400'
    case 'en_cours': return 'bg-blue-500'
    case 'a_valider': return 'bg-red-500'
    case 'valide_ic': return 'bg-orange-500'
    case 'valide_manager': return 'bg-cyan-500'
    case 'envoye_rh': return 'bg-emerald-500'
    default: return 'bg-gray-400'
  }
}

function getStatusLabel(status: MonthStatus): string {
  switch (status) {
    case 'avenir': return 'À venir'
    case 'en_cours': return 'En cours'
    case 'a_valider': return 'À valider'
    case 'valide_ic': return 'Validé par moi'
    case 'valide_manager': return 'Validé par N+1'
    case 'envoye_rh': return 'Envoyé RH'
    default: return ''
  }
}

function getStatusBadgeClass(status: MonthStatus): string {
  const base = 'px-2 py-0.5 rounded text-xs font-medium text-white '
  switch (status) {
    case 'envoye_rh': return base + 'bg-emerald-500'
    case 'valide_manager': return base + 'bg-cyan-500'
    case 'valide_ic': return base + 'bg-orange-500'
    case 'a_valider': return base + 'bg-red-500'
    case 'en_cours': return base + 'bg-blue-500'
    case 'avenir': return base + 'bg-gray-400'
    default: return base + 'bg-gray-400'
  }
}

const selectedYear = computed(() => dateSelected.value.getFullYear())
const selectedMonth = computed(() => dateSelected.value.getMonth())

const monthOptions = computed(() => {
  const year = selectedYear.value
  const now = new Date()
  const pastMonths: { month: number; label: string; amount: number; status: MonthStatus; variable: Variable | null }[] = []
  const futureMonths: { month: number; label: string; amount: number; status: MonthStatus; variable: Variable | null }[] = []
  for (let m = 0; m < 12; m++) {
    const variable = variablesForYear.value.find((v: any) => {
      const p = new Date(v.period)
      return p.getFullYear() === year && p.getMonth() === m
    }) ?? null
    const status = getMonthStatus(year, m, variable)
    const amount = variable?.amount ?? 0
    const item = { month: m, label: MONTHS_FR[m], amount, status, variable }
    if (year > now.getFullYear() || (year === now.getFullYear() && m > now.getMonth())) {
      futureMonths.push(item)
    } else {
      pastMonths.push(item)
    }
  }
  pastMonths.sort((a, b) => b.month - a.month)
  futureMonths.sort((a, b) => a.month - b.month)
  return { past: pastMonths, future: futureMonths }
})

const currentMonthLabel = computed(() => MONTHS_FR[selectedMonth.value])
const currentMonthStatus = computed(() => {
  // Priorité à currentVariable (source la plus à jour pour le mois sélectionné)
  if (currentVariable.value) {
    const p = new Date(currentVariable.value.period)
    if (p.getFullYear() === selectedYear.value && p.getMonth() === selectedMonth.value) {
      return getMonthStatus(selectedYear.value, selectedMonth.value, currentVariable.value)
    }
  }
  const variable = variablesForYear.value.find((v: any) => {
    const p = new Date(v.period)
    return p.getFullYear() === selectedYear.value && p.getMonth() === selectedMonth.value
  }) ?? null
  return getMonthStatus(selectedYear.value, selectedMonth.value, variable)
})

async function loadVariablesForYear() {
  variablesForYear.value = await variableStore.fetchVariablesForYear(selectedYear.value)
}

function selectMonth(month: number) {
  dateSelected.value = new Date(selectedYear.value, month, 15)
  monthDropdownOpen.value = false
}

const MIN_YEAR = 2026
const MAX_YEAR = 2027
const canGoPrevYear = computed(() => selectedYear.value > MIN_YEAR)
const canGoNextYear = computed(() => selectedYear.value < MAX_YEAR)

function changeYear(delta: number) {
  const newYear = selectedYear.value + delta
  if (newYear < MIN_YEAR || newYear > MAX_YEAR) return
  dateSelected.value = new Date(newYear, selectedMonth.value, 15)
}

const monthDropdownRef = ref<HTMLElement | null>(null)
function handleClickOutside(e: MouseEvent) {
  if (monthDropdownOpen.value && monthDropdownRef.value && !monthDropdownRef.value.contains(e.target as Node)) {
    monthDropdownOpen.value = false
  }
  if (statusPopoverOpen.value) {
    statusPopoverOpen.value = false
  }
}
const showModalEdit = ref(false)
const variableEditParams = ref({})

const pendingValidationFormatted = computed(() =>
  pendingValidation.value.map((v: any) => ({
    id: v.uuid,
    salesEngineer: v.salesEngineer ? `${v.salesEngineer.firstName || ''} ${v.salesEngineer.lastName || ''}`.trim() : '-',
    period: v.period ? new Date(v.period).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '-',
    totalCounterCommission: v.totalCounterCommission,
    totalCounterMarge: v.totalCounterMarge,
    variable: v.variable,
    recovery: v.recovery ?? 0,
    complement: v.complement ?? 0,
    amount: v.amount ?? 0,
    variableStatus: v.variableStatus,
  })),
)

const variableFormated = computed(() => {
  return variables.value.map((variable: any) => {
    return {
      id: variable.uuid,
      salesEngineer: `${variable.salesEngineer.firstName} ${variable.salesEngineer.lastName}`,
      totalCounterCommission: variable.totalCounterCommission,
      totalCounterMarge: variable.totalCounterMarge,
      reachedRate: variable.reachedRate,
      variable: variable.variable,
      recovery: variable.recovery,
      complement: variable.complement,
      amount: variable.amount,
      variableStatus: variable.variableStatus,
      period: variable.period,
    }
  })
})

const commissionsFormated = computed(() => {
  const commissionVariables = currentVariable.value?.commissionVariables || []
  const commissions = commissionVariables.map((cv: any) => cv.commission).filter(Boolean)
  const reachedRate = currentVariable.value?.reachedRate ?? 1
  return commissions.map((c: any) => {
    const quote = c.order?.quote
    const company = quote?.company
    const typeContrat = quote?.typeContrat
    const subscriptionDuration = quote?.subscriptionDuration
    const paymentMode = quote?.paymentMode
    const iav = quote?.iav
    const iavLabel = iav ? [iav.firstName, iav.lastName].filter(Boolean).join(' ').trim() || '-' : '-'
    const se = c.salesEngineer
    const salesEngineerLabel = se ? [se.firstName, se.lastName].filter(Boolean).join(' ').trim() || '-' : '-'
    return {
      uuid: c.uuid,
      orderUuid: c.order?.uuid ?? null,
      salesEngineer: salesEngineerLabel,
      raisonSociale: company?.name ?? '-',
      reference: quote?.quoteReference ?? '-',
      typeContrat: typeContrat?.label ?? '-',
      typeContratCoef: c.contractCoef,
      duree: subscriptionDuration?.label ?? '-',
      dureeCoef: c.durationCoef,
      facturation: paymentMode?.type ?? '-',
      facturationCoef: c.paymentCoef,
      iav: iavLabel,
      iavCoef: c.iavCoef,
      isLead: c.isLead ? 'Oui' : 'Non',
      margeCompteur: c.margeCompteur,
      commission: (c.commission ?? 0) * reachedRate,
    }
  })
})

const regulationsFormatted = computed(() => {
  const regulationVariables = currentVariable.value?.regulationVariables || []
  return regulationVariables
    .map((rv: any) => rv.regulation)
    .filter(Boolean)
    .map((r: any) => {
      const se = r.salesEngineer
      const salesEngineerLabel = se ? [se.firstName, se.lastName].filter(Boolean).join(' ').trim() || '-' : '-'
      const authorLabel = r.author ? [r.author.firstName, r.author.lastName].filter(Boolean).join(' ').trim() || '-' : '-'
      return {
        uuid: r.uuid,
        salesEngineer: salesEngineerLabel,
        raisonSociale: r.quote?.company?.name ?? '-',
        reference: r.quote?.quoteReference ?? '-',
        family: r.family ?? '-',
        amount: r.amount ?? 0,
        contractCoef: r.contractCoef ?? 1,
        durationCoef: r.durationCoef ?? 1,
        paymentCoef: r.paymentCoef ?? 1,
        iavCoef: r.iavCoef ?? 1,
        reason: r.reason ?? '-',
        status: r.status ?? 'INIT',
        author: authorLabel,
        validatedAt: r.validatedAt ? new Date(r.validatedAt).toLocaleDateString('fr-FR') : '-',
      }
    })
})

// Payplan à paliers (BU UNIXO_ACR…) : rémunération par prime forfaitaire de marge cumulée.
const isPalierPayplan = computed(() => (currentVariable.value?.payplan?.data as any)?.kind === 'PALIER')
// Payplan « prime équipe % » (BU UNIXO_CUSTOMER_SUCCESS).
const isTeamPercentPayplan = computed(() => (currentVariable.value?.payplan?.data as any)?.kind === 'TEAM_PERCENT')
const teamPercentData = computed(() => (currentVariable.value?.payplan?.data as any) ?? null)
// Payplans « spéciaux » (hors modèle commission/coefficients) : on masque coefs & commission.
const isSpecialPayplan = computed(() => isPalierPayplan.value || isTeamPercentPayplan.value)

const PALIER_ICONS = [AcademicCapIcon, RocketLaunchIcon, StarIcon, SparklesIcon, TrophyIcon]
const PALIER_COLORS = ['#4ade80', '#22d3ee', '#3b82f6', '#8b5cf6', '#f59e0b']

type PalierDisplay = {
  key: number
  nom: string
  pourcentage: number
  montant: number
  prime: number
  isObjectif: boolean
  icone: any
  couleur: string
}

const paliers = computed<PalierDisplay[]>(() => {
  const payplan = currentVariable.value?.payplan
  if (!payplan?.data) return []
  const data: any = payplan.data

  // Modèle à paliers : chaque marche de marge cumulée déclenche une prime forfaitaire (pas un %).
  if (data.kind === 'PALIER') {
    const list = Array.isArray(data.paliers) ? data.paliers : []
    const lastIndex = list.length - 1
    return list.map((p: any, index: number) => ({
      key: index,
      nom: `Palier ${index + 1}`,
      pourcentage: 0,
      montant: p.marge ?? 0,
      prime: p.prime ?? 0,
      isObjectif: index === lastIndex,
      icone: PALIER_ICONS[index % PALIER_ICONS.length],
      couleur: PALIER_COLORS[index % PALIER_COLORS.length],
    }))
  }

  const results = [
    { key: 0, nom: 'Rookie', pourcentage: 0, montant: 0, prime: 0, isObjectif: false, icone: AcademicCapIcon, couleur: '#4ade80' },
    { key: 1, nom: 'Warrior', pourcentage: 0, montant: 0, prime: 0, isObjectif: false, icone: RocketLaunchIcon, couleur: '#22d3ee' },
    { key: 2, nom: 'Elite', pourcentage: 0, montant: 0, prime: 0, isObjectif: false, icone: StarIcon, couleur: '#3b82f6' },
    { key: 3, nom: 'Senior', pourcentage: 0, montant: 0, prime: 0, isObjectif: true, icone: SparklesIcon, couleur: '#8b5cf6' },
    { key: 4, nom: 'Légende', pourcentage: 0, montant: 0, prime: 0, isObjectif: false, icone: TrophyIcon, couleur: '#f59e0b' },
  ]
  const nbEtp = currentVariable.value?.salesEngineer?.metadata?.nbEtp || 0
  const nbEtpNewbie = currentVariable.value?.salesEngineer?.metadata?.nbEtpNewbie ?? undefined
  const isSelectedManager = nbEtp > 0 || (nbEtpNewbie != null && nbEtpNewbie > 0)
  const isNewbie = currentVariable.value?.salesEngineer?.metadata?.isNewbie === true
  const steps = isSelectedManager ? data.managerStep : isNewbie ? data.newbieStep : data.icStep
  // Mapper les steps sur les paliers dans l'ordre
  return results.map((palier, index) => {
    if (steps && steps[index]) {
      const step = steps[index]
      const marge = step.marge ?? 0
      const margeNewbie = step.margeNewbie ?? 0
      const montant = isSelectedManager ? (nbEtpNewbie != null && nbEtpNewbie > 0 ? marge * nbEtp + margeNewbie * nbEtpNewbie : marge * nbEtp) : marge
      return {
        ...palier,
        montant: montant || 0,
        pourcentage: Math.round(steps[index].percent * 100 * 100) / 100 || 0,
      }
    }
    return palier
  })
})

// Booster d'équipe et objectif (modèle à paliers ACR).
const teamBooster = computed(() => {
  const data: any = currentVariable.value?.payplan?.data
  return Array.isArray(data?.teamBooster) ? data.teamBooster : []
})
const objectifMarge = computed(() => {
  const data: any = currentVariable.value?.payplan?.data
  if (data?.objectifMarge) return data.objectifMarge
  const ps = paliers.value
  return ps.length ? ps[ps.length - 1].montant : 0
})

const totalMargeCompteur = computed(() => currentVariable.value?.totalCounterMarge || 0)

const avgCommissionCoefs = computed(() => {
  const commissionVariables = currentVariable.value?.commissionVariables || []
  const commissions = commissionVariables.map((cv: any) => cv.commission).filter(Boolean)
  const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0)
  const avg = (arr: number[]) => (arr.length ? sum(arr) / arr.length : 0)
  const durationCoefs = commissions.map((c: any) => c.durationCoef).filter((v: any) => v != null)
  const contractCoefs = commissions.map((c: any) => c.contractCoef).filter((v: any) => v != null)
  const paymentCoefs = commissions.map((c: any) => c.paymentCoef).filter((v: any) => v != null)
  const iavCoefs = commissions.map((c: any) => c.iavCoef).filter((v: any) => v != null)
  return {
    coefDuration: durationCoefs.length ? avg(durationCoefs) : null,
    coefContract: contractCoefs.length ? avg(contractCoefs) : null,
    coefPayment: paymentCoefs.length ? avg(paymentCoefs) : null,
    coefIAV: iavCoefs.length ? avg(iavCoefs) : null,
    count: commissions.length,
  }
})

const canValidateVariable = computed(() => {
  if (currentVariable.value?.variableStatus !== 'INIT') return false
  const period = currentVariable.value?.period
  if (!period) return false
  const p = new Date(period)
  const fromDate = new Date(p.getFullYear(), p.getMonth() + 1, 8)
  return new Date() >= fromDate
})

const variableStatusInfo = computed(() => {
  const v = currentVariable.value
  if (!v) return null
  const status = v.variableStatus as string
  const steps = [
    {
      key: 'INIT',
      label: 'Variable calculé',
      done: true,
      actor: null as string | null,
      date: v.period ? new Date(v.period).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : null,
    },
    {
      key: 'VALIDATION_IC',
      label: 'Validé par le commercial',
      done: status === 'VALIDATION_IC' || status === 'VALIDATION_MANAGER' || status === 'SENT_TO_LUCCA',
      actor: v.validatedBySalesEngineer ? `${v.validatedBySalesEngineer.firstName ?? ''} ${v.validatedBySalesEngineer.lastName ?? ''}`.trim() : null,
      date: v.validatedBySalesEngineerAt ? new Date(v.validatedBySalesEngineerAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null,
    },
    {
      key: 'VALIDATION_MANAGER',
      label: 'Validé par le manager',
      done: status === 'VALIDATION_MANAGER' || status === 'SENT_TO_LUCCA',
      actor: v.validatedByManager ? `${v.validatedByManager.firstName ?? ''} ${v.validatedByManager.lastName ?? ''}`.trim() : null,
      date: v.validatedByManagerAt ? new Date(v.validatedByManagerAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null,
    },
    {
      key: 'SENT_TO_LUCCA',
      label: 'Envoyé aux RH',
      done: status === 'SENT_TO_LUCCA',
      actor: null,
      date: v.sentToLuccaAt ? new Date(v.sentToLuccaAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null,
    },
  ]
  return { status, steps }
})

const validationMessage = computed(() => {
  const v = currentVariable.value
  if (!v) return null
  const status = v.variableStatus as string
  switch (status) {
    case 'INIT':
      return canValidateVariable.value
        ? { type: 'action', title: 'Variable à valider', message: 'Votre variable est prêt. En le validant, il sera transmis à votre manager pour approbation.' }
        : { type: 'info', title: 'Variable en cours', message: 'Votre variable est en cours de calcul. Il sera disponible à la validation après le 8 du mois suivant.' }
    case 'VALIDATION_IC':
      return { type: 'wait', title: 'En attente de validation N+1', message: 'Vous avez validé votre variable. Il est maintenant en attente de validation par votre manager.' }
    case 'VALIDATION_MANAGER':
      return { type: 'success', title: 'Validé par votre manager', message: 'Votre variable a été validé par votre manager. Il sera prochainement transmis aux RH.' }
    case 'SENT_TO_LUCCA':
      return { type: 'done', title: 'Envoyé aux RH', message: 'Votre variable a été transmis aux RH pour traitement sur votre bulletin de paie.' }
    default:
      return null
  }
})

const statusPopoverOpen = ref(false)

const nbEtpDisplay = computed(() => {
  const nbEtp = currentVariable.value?.salesEngineer?.metadata?.nbEtp || 0
  const nbEtpNewbie = currentVariable.value?.salesEngineer?.metadata?.nbEtpNewbie ?? undefined
  if (nbEtp > 0 || (nbEtpNewbie != null && nbEtpNewbie > 0)) {
    const parts: string[] = []
    if (nbEtp > 0) parts.push(`${nbEtp} ETP`)
    if (nbEtpNewbie != null && nbEtpNewbie > 0) parts.push(`${nbEtpNewbie} Newbie`)
    return parts.join(' + ')
  }
  return null
})

const palierActuel = computed(() => {
  const montant = totalMargeCompteur.value
  return [...paliers.value].reverse().find((p) => montant >= p.montant)
})

const prochainPalier = computed(() => {
  const index = paliers.value.findIndex((p) => p.nom === palierActuel.value?.nom)
  return index < paliers.value.length - 1 ? paliers.value[index + 1] : null
})

const montantRestantPourProchain = computed(() => {
  if (!prochainPalier.value) return 0
  return Math.max(prochainPalier.value.montant - totalMargeCompteur.value, 0)
})

const pourcentageProgression = computed(() => {
  if (paliers.value.length === 0) return 0
  const minMontant = paliers.value[0].montant
  const maxMontant = paliers.value[paliers.value.length - 1].montant
  const progression = ((totalMargeCompteur.value - minMontant) / (maxMontant - minMontant)) * 100
  return Math.min(Math.max(progression, 0), 100)
})

const getPositionPalier = (montant: number) => {
  const minMontant = paliers.value[0].montant
  const maxMontant = paliers.value[paliers.value.length - 1].montant
  const position = ((montant - minMontant) / (maxMontant - minMontant)) * 100
  return Math.min(Math.max(position, 0), 100)
}

onMounted(async () => {
  if (!boostCoreStore.user) await boostCoreStore.fetchMe()
  if (canSelectUser.value && boostCoreStore.user) {
    const queryUser = route.query.userUuid as string | undefined
    variableStore.selectedUserUuid = queryUser || boostCoreStore.user.uuid
    if (isSuperAdmin.value) {
      if (usersStore.users.length === 0) await usersStore.fetchUsers()
    } else {
      if (!myTeamStore.isLoaded) await myTeamStore.fetch()
    }
  } else {
    await variableStore.fetchMyVariables()
  }
  if (isManagerConnected.value || canValidateForTeam.value) {
    pendingValidation.value = await variableStore.fetchPendingValidation()
  }
  const y = dateSelected.value.getFullYear()
  if (y < MIN_YEAR || y > MAX_YEAR) {
    dateSelected.value = new Date(MIN_YEAR, Math.min(11, dateSelected.value.getMonth()), 15)
  }
console.log()
  await loadVariablesForYear()
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

watch([selectedYear, () => variableStore.selectedUserUuid], () => {
  loadVariablesForYear()
})

// Synchronise variablesForYear quand currentVariable change (montant, statut)
watch(currentVariable, (cv) => {
  if (!cv) return
  const p = new Date(cv.period)
  const idx = variablesForYear.value.findIndex((v: any) => {
    const vp = new Date(v.period)
    return vp.getFullYear() === p.getFullYear() && vp.getMonth() === p.getMonth()
  })
  if (idx >= 0) {
    variablesForYear.value[idx] = cv
  } else {
    variablesForYear.value.push(cv)
  }
})

watch(
  () => boostCoreStore.user,
  (user) => {
    if (canSelectUser.value && user && variableStore.selectedUserUuid === null) {
      variableStore.selectedUserUuid = user.uuid
    }
  },
  { immediate: true },
)

const openModalEdit = (params: Variable) => {
  showModalEdit.value = true
  variableEditParams.value = params
}

const showConfirmValidate = ref(false)
const isValidating = ref(false)

const confirmValidateVariable = () => {
  showConfirmValidate.value = true
}

const onConfirmValidateVariable = async () => {
  isValidating.value = true
  try {
    await variableStore.validateVariable(currentVariable.value?.uuid)
    showConfirmValidate.value = false
    await loadVariablesForYear()
  } finally {
    isValidating.value = false
  }
}

const onVariableEditClose = () => {
  showModalEdit.value = false
  loadVariablesForYear()
}

const validateVariable = async (uuid: number) => {
  await variableStore.validateVariable(uuid, true)
  await loadVariablesForYear()
}

const validatePendingVariable = async (uuid: string) => {
  await variableStore.validateVariable(uuid as any, true)
  pendingValidation.value = await variableStore.fetchPendingValidation()
  await loadVariablesForYear()
}

const orderLinkRenderer = (params: any) => {
  if (!params.value) return '-'
  const a = document.createElement('a')
  a.href = `/dashboard/order/${params.value}`
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  a.textContent = 'Voir'
  a.style.color = '#175a7c'
  a.style.textDecoration = 'underline'
  a.style.fontWeight = '500'
  a.style.fontSize = '0.875rem'
  return a
}

const typeCoefRenderer = (params: any) => {
  const coefField = params.coefField
  const labelField = coefField?.replace('Coef', '') || params.colDef?.field
  const label = params.data?.[labelField] || 'Non défini'
  const coef = params.data?.[coefField]
  const taux = coef != null ? `Coef : ${coef.toFixed(1)}` : '-'

  const container = document.createElement('div')
  container.style.whiteSpace = 'pre-line'
  container.style.lineHeight = '1.4'
  container.innerHTML = `${label}<br><span style="font-size: 0.75rem; color: #6b7280;">${taux}</span>`

  return container
}
</script>

<template>
  <div class="w-full flex flex-col gap-6 p-8 min-h-screen">
    <div v-if="(isManagerConnected || canValidateForTeam) && pendingValidationFormatted.length > 0" class="bg-amber-50 rounded-xl shadow-sm p-8 border border-amber-200">
      <h2 class="text-2xl font-bold text-amber-900 mb-6">Variables à valider pour mes managés directs</h2>
      <GridVue
        :list="pendingValidationFormatted"
        :getRowStyle="() => ({ backgroundColor: 'var(--color-amber-100)' })"
        :cols="[
          { field: 'salesEngineer', headerName: 'Commercial', cellClass: 'border-0', filter: false },
          { field: 'period', headerName: 'Période', cellClass: 'border-0', filter: false },
          {
            field: 'totalCounterMarge',
            headerName: 'Marge Brut',
            cellClass: 'border-0',
            filter: false,
            valueFormatter: (p: any) =>
              p.value != null ? `${Number(p.value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-',
          },
          {
            field: 'variable',
            headerName: 'Variable',
            cellClass: 'border-0',
            filter: false,
            valueFormatter: (p: any) =>
              p.value != null ? `${Number(p.value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-',
          },
          ...(canEditAdjustments
            ? [
                {
                  field: 'recovery',
                  headerName: 'Reprise',
                  cellClass: 'border-0',
                  filter: false,
                  valueFormatter: (p: any) =>
                    p.value != null ? `${Number(p.value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-',
                },
                {
                  field: 'complement',
                  headerName: 'Complément',
                  cellClass: 'border-0',
                  filter: false,
                  valueFormatter: (p: any) =>
                    p.value != null ? `${Number(p.value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-',
                },
              ]
            : []),
          {
            field: 'amount',
            headerName: 'Total Variable',
            cellClass: 'border-0',
            filter: false,
            valueFormatter: (p: any) =>
              p.value != null ? `${Number(p.value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-',
          },
          {
            field: 'action',
            headerName: 'Actions',
            resizable: false,
            filter: false,
            minWidth: 120,
            maxWidth: 120,
            cellRenderer: VariableGridActions,
            cellRendererParams: {
              validateVariable: (id: string) => validatePendingVariable(id),
              tooltipPosition: 'left',
            },
            sortable: false,
            cellClass: 'border-0 grid-action',
          },
        ]"
      />
    </div>

    <div class="flex justify-between items-center flex-wrap gap-4">
      <Bs2Title
        v-if="isSuperAdmin"
        title="Variables"
        add-classes="cursor-default select-none"
        @click="onTitleClick"
      />
      <Bs2Title v-else title="Variables" />
      <div ref="monthDropdownRef" class="relative flex items-center    overflow-visible">
        <div>

        <button
          type="button"
          @click="changeYear(-1)"
          :disabled="!canGoPrevYear"
          class="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          aria-label="Année précédente"
        >
          <ChevronLeftIcon class="w-5 h-5 text-gray-600" />
        </button>
        </div>
        <div class="flex items-center flex-col px-16">
          <span class="text-xs text-gray-400 px-2">{{ selectedYear }}</span>
        <button
          type="button"
          @click="monthDropdownOpen = !monthDropdownOpen"
          class="flex items-center gap-2 px-3 py-2 border rounded-lg border-gray-300 hover:bg-gray-50 transition-colors min-w-[200px]"
        >
          <CalendarIcon class="w-4 h-4 text-gray-500" />
          <span class="font-medium text-gray-900 text-sm">{{ currentMonthLabel }}</span>
          <span :class="getStatusBadgeClass(currentMonthStatus)">
            {{ getStatusLabel(currentMonthStatus) }}
          </span>
          <ChevronDownIcon class="w-4 h-4 text-gray-500 ml-auto" />
        </button>
        </div>
        <div>
          <button
            type="button"
            @click="changeYear(1)"
            :disabled="!canGoNextYear"
            class="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            aria-label="Année suivante"
          >
            <ChevronRightIcon class="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div
          v-if="monthDropdownOpen"
          class="absolute left-1/2 -translate-x-1/2 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2 max-h-80 overflow-y-auto"
        >
          <template v-for="item in monthOptions.past" :key="'past-' + item.month">
            <button
              type="button"
              @click="selectMonth(item.month)"
              class="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 text-left"
              :class="{ 'bg-blue-50': item.month === selectedMonth }"
            >
              <div class="flex items-center gap-2">
                <span :class="['w-2.5 h-2.5 rounded-full shrink-0', getStatusColor(item.status)]" />
                <span class="font-medium">{{ item.label }} {{ selectedYear }}</span>
              </div>
              <span class="text-sm text-gray-600">{{ item.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} €</span>
            </button>
          </template>
          <div class="border-t border-gray-200 my-2" />
          <template v-for="item in monthOptions.future" :key="'future-' + item.month">
            <div
              class="w-full flex items-center justify-between px-4 py-2 text-gray-400 cursor-not-allowed"
            >
              <div class="flex items-center gap-2">
                <span :class="['w-2.5 h-2.5 rounded-full shrink-0', getStatusColor(item.status)]" />
                <span class="font-medium">{{ item.label }} {{ selectedYear }}</span>
              </div>
              <span class="text-sm"></span>
            </div>
          </template>
        </div>
      </div>
      <div class="flex items-center gap-3 flex-wrap min-w-[200px] justify-end">
        <SelectInput
          v-if="canSelectUser"
          v-model="variableStore.selectedUserUuid"
          label="Commercial"
          :options="userOptions"
          placeholder="Sélectionner un commercial"
          class="min-w-[200px]"
        />
      </div>
    </div>

    <!-- Bloc Statut & Validation -->
    <div v-if="currentVariable && validationMessage" class="rounded-xl shadow-sm border p-5 flex items-start gap-4"
      :class="{
        'bg-blue-50 border-blue-200': validationMessage.type === 'info',
        'bg-amber-50 border-amber-200': validationMessage.type === 'action',
        'bg-orange-50 border-orange-200': validationMessage.type === 'wait',
        'bg-cyan-50 border-cyan-200': validationMessage.type === 'success',
        'bg-emerald-50 border-emerald-200': validationMessage.type === 'done',
      }"
    >
      <div class="flex-1">
        <div class="flex items-center gap-3 mb-1">
          <h3 class="font-bold text-lg"
            :class="{
              'text-blue-900': validationMessage.type === 'info',
              'text-amber-900': validationMessage.type === 'action',
              'text-orange-900': validationMessage.type === 'wait',
              'text-cyan-900': validationMessage.type === 'success',
              'text-emerald-900': validationMessage.type === 'done',
            }"
          >{{ validationMessage.title }}</h3>
          <span :class="getStatusBadgeClass(currentMonthStatus)">{{ getStatusLabel(currentMonthStatus) }}</span>

          <!-- Popover historique -->
          <div class="relative">
            <button @click.stop="statusPopoverOpen = !statusPopoverOpen" class="p-1 rounded-full hover:bg-white/60 transition-colors" title="Historique de validation">
              <InformationCircleIcon class="w-5 h-5 text-gray-500" />
            </button>
            <div v-if="statusPopoverOpen && variableStatusInfo" class="absolute left-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4">
              <h4 class="font-semibold text-gray-900 mb-3 text-sm">Parcours de validation</h4>
              <div class="space-y-3">
                <div v-for="(step, i) in variableStatusInfo.steps" :key="step.key" class="flex items-start gap-3">
                  <div class="flex flex-col items-center">
                    <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0"
                      :class="step.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-gray-300 text-gray-400'"
                    >
                      <CheckIcon v-if="step.done" class="w-3.5 h-3.5" />
                      <span v-else>{{ i + 1 }}</span>
                    </div>
                    <div v-if="i < variableStatusInfo.steps.length - 1" class="w-0.5 h-4 mt-1" :class="step.done ? 'bg-emerald-300' : 'bg-gray-200'" />
                  </div>
                  <div class="min-w-0">
                    <div class="text-sm font-medium" :class="step.done ? 'text-gray-900' : 'text-gray-400'">{{ step.label }}</div>
                    <div v-if="step.actor" class="text-xs text-gray-500">par {{ step.actor }}</div>
                    <div v-if="step.date" class="text-xs text-gray-400">{{ step.date }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p class="text-sm" :class="{
          'text-blue-700': validationMessage.type === 'info',
          'text-amber-700': validationMessage.type === 'action',
          'text-orange-700': validationMessage.type === 'wait',
          'text-cyan-700': validationMessage.type === 'success',
          'text-emerald-700': validationMessage.type === 'done',
        }">{{ validationMessage.message }}</p>
      </div>
      <ButtonDaisy
        v-if="validationMessage.type === 'action' && canValidateVariable"
        text="Valider mon variable"
        variant="warning"
        class="font-semibold shadow-md shrink-0"
        @click="confirmValidateVariable"
        :icon="CheckIcon"
      />
    </div>

    <!-- Barre d'objectif mensuel style Image -->
    <PalierProgress
      v-if="isPalierPayplan && currentVariable"
      :paliers="paliers"
      :team-booster="teamBooster"
      :current="totalMargeCompteur"
      :objectif="objectifMarge"
      :year="selectedYear"
      :reloadable="currentVariable?.variableStatus !== 'SENT_TO_LUCCA'"
      @reload="variableStore.reloadVariable(currentVariable?.uuid)"
    />
    <TeamPercentProgress
      v-else-if="isTeamPercentPayplan && currentVariable"
      :data="teamPercentData"
      :member-marge="totalMargeCompteur"
      :taux="currentVariable?.reachedRate ?? 0"
      :part="currentVariable?.variable ?? 0"
      :year="selectedYear"
      :reloadable="currentVariable?.variableStatus !== 'SENT_TO_LUCCA'"
      @reload="variableStore.reloadVariable(currentVariable?.uuid)"
    />
    <div v-else class="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
      <div class="flex justify-between items-start mb-6">
        <div>

          <div class="flex items-center gap-4">
            <span class="text-3xl font-extrabold text-gray-900">{{ totalMargeCompteur.toLocaleString('fr-FR') || 0 }} €</span>
            <span v-if="palierActuel && !isSpecialPayplan" class="px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
              Taux actuel : {{ palierActuel.pourcentage }}%
            </span>
            <span v-else-if="palierActuel && isPalierPayplan" class="px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
              Prime palier : {{ palierActuel.prime.toLocaleString('fr-FR') }} €
            </span>
          </div>
          <div class="text-gray-500 text-sm mb-1 uppercase tracking-wider font-semibold">Marge brute totale</div>
        </div>
        <div v-if="nbEtpDisplay" class="flex items-center gap-2 text-gray-600">
          <UserGroupIcon class="w-5 h-5" />
          <span class="text-sm font-medium">{{ nbEtpDisplay }}</span>
        </div>
        <div class="text-right">

          <div v-if="palierActuel" class="flex items-center gap-2 justify-end">
            <component :is="palierActuel.icone" class="w-6 h-6 text-yellow-500" />
            <span class="text-xl font-bold text-gray-800">{{ palierActuel.nom }}</span>
          </div>
          <div class="text-gray-500 text-xs uppercase tracking-wider mb-1 font-semibold">Palier actuel</div>
        </div>
      </div>

      <!-- Barre de progression avec paliers -->
      <div class="relative mt-10 mb-40 px-10">
        <!-- Fond de la barre -->
        <div class="absolute left-10 right-10 h-4 bg-gray-100 rounded-full top-1/2 -translate-y-1/2 border border-gray-200"></div>
        <!-- Progression active -->
        <div
          class="absolute left-10 h-4 bg-linear-to-r from-emerald-400 via-cyan-500 to-blue-500 rounded-full top-1/2 -translate-y-1/2 transition-all duration-1000 ease-out shadow-sm"
          :style="{ width: `calc((100% - 80px) * ${pourcentageProgression} / 100)` }"
        ></div>

        <!-- Icônes et Labels alignés sur la barre -->
        <div class="absolute left-10 right-10 -top-6.5 -translate-y-1/2 pointer-events-none">
          <div
            v-for="palier in paliers"
            :key="palier.nom"
            :class="{ 'absolute flex flex-col items-center -translate-x-1/2': true, '-top-7': palier.isObjectif }"
            :style="{ left: `${getPositionPalier(palier.montant)}%` }"
          >
            <div v-if="palier.isObjectif" class="text-md font-medium whitespace-nowrap">Objectif</div>
            <!-- Icône -->
            <div
              class="w-14 h-14 rounded-full flex items-center justify-center border-4 transition-all duration-500 z-10 shadow-sm bg-white"
              :class="[
                totalMargeCompteur >= palier.montant ? 'border-emerald-500 text-emerald-600 scale-110' : 'border-gray-200 text-gray-400 opacity-60',
              ]"
            >
              <component :is="palier.icone" class="w-7 h-7" />
            </div>

            <!-- Labels -->
            <div class="mt-4 text-center">
              <div class="text-sm font-bold" :class="totalMargeCompteur >= palier.montant ? 'text-gray-900' : 'text-gray-400'">
                <template v-if="isPalierPayplan">{{ palier.prime.toLocaleString('fr-FR') }} €</template>
                <template v-else>{{ palier.pourcentage }}%</template>
              </div>
              <div class="text-xs font-medium text-gray-500 whitespace-nowrap">{{ palier.montant.toLocaleString('fr-FR') }}€</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer infos -->
      <div class="flex justify-end items-center text-sm border-t border-gray-100 pt-4">
        <IconWithTooltip
          v-if="currentVariable?.variableStatus !== 'SENT_TO_LUCCA'"
          text="Recharger"
          variant="secondary"
          class="mr-4"
          @click="variableStore.reloadVariable(currentVariable?.uuid)"
          :icon="ArrowPathIcon"
        />

        <div v-if="prochainPalier" class="text-gray-500">
          Plus que
          <span class="text-cyan-600 font-bold text-lg">{{ montantRestantPourProchain.toLocaleString('fr-FR') || 0 }} €</span>
          pour débloquer
          <span v-if="isPalierPayplan" class="text-gray-900 font-bold">une prime de {{ prochainPalier.prime.toLocaleString('fr-FR') }} €</span>
          <span v-else class="text-gray-900 font-bold">{{ prochainPalier.pourcentage }}%</span>
        </div>
        <div v-else class="text-emerald-600 font-bold flex items-center gap-2">
          <TrophyIcon class="w-5 h-5" /> Palier maximum atteint ! Félicitations !
        </div>
      </div>
    </div>

    <!-- Section Calcul de la Variable -->
    <div class="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
      <div class="grid grid-cols-3 gap-4 items-start mb-6">
        <h2 class="text-xl font-bold text-gray-900">Détail de votre variable</h2>
        <div class="flex justify-center">
          <Tooltip
            fit
            :tip="`Total = Variable de base (${(currentVariable?.variable || 0).toLocaleString('fr-FR')} €) + Recovery (${(currentVariable?.recovery || 0).toLocaleString('fr-FR')} €) + Complément (${(currentVariable?.complement || 0).toLocaleString('fr-FR')} €)`"
          >
            <div class="bg-green-50 rounded-xl px-8 border-2 border-green-300 cursor-help">
              <span class="text-3xl font-extrabold text-green-900">
                {{ currentVariable?.amount ? currentVariable.amount.toLocaleString('fr-FR') : 0 }} €
              </span>
            </div>
          </Tooltip>
        </div>
        <div></div>
      </div>
      <div class="flex flex-wrap items-stretch gap-4">
        <!-- Variable de base -->
        <div class="flex-1 min-w-0">
          <Tooltip tip="Base de calcul de la variable selon les paliers atteints">
            <div class="bg-blue-50 rounded-lg p-5 border-2 border-blue-200 h-full cursor-help">
            <div class="text-xs uppercase tracking-wider font-semibold text-blue-700 mb-3">Variable</div>
            <div class="text-2xl font-extrabold text-blue-900">{{ currentVariable?.variable.toLocaleString('fr-FR') || 0 }} €</div>
            </div>
          </Tooltip>
        </div>

        <!-- Recovery -->
        <div class="flex-1 min-w-0">
          <Tooltip tip="Montant de récupération sur les commissions">
            <div class="bg-orange-50 rounded-lg p-5 border-2 border-orange-200 h-full cursor-help">
              <div class="text-xs uppercase tracking-wider font-semibold text-orange-700 mb-3">Recovery</div>
              <div class="text-2xl font-extrabold text-orange-900">
                {{ currentVariable?.recovery ? currentVariable.recovery.toLocaleString('fr-FR') : 0 }} €
              </div>
            </div>
          </Tooltip>
        </div>

        <!-- Complément -->
        <div class="flex-1 min-w-0">
          <Tooltip tip="Complément lié aux commissions validées">
            <div class="bg-purple-50 rounded-lg p-5 border-2 border-purple-200 h-full cursor-help">
              <div class="text-xs uppercase tracking-wider font-semibold text-purple-700 mb-3">Complément</div>
              <div class="text-2xl font-extrabold text-purple-900">
                {{ currentVariable?.complement ? currentVariable.complement.toLocaleString('fr-FR') : 0 }} €
              </div>
            </div>
          </Tooltip>
        </div>

        <!-- Coef. durée -->
        <div v-if="currentVariable && !isSpecialPayplan" class="flex-1 min-w-0">
          <Tooltip tip="Moyenne du coefficient durée sur vos commissions">
            <div class="bg-slate-50 rounded-lg p-5 border-2 border-slate-200 h-full cursor-help">
              <div class="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-3">Durée</div>
              <div class="text-2xl font-extrabold text-slate-900">{{ avgCommissionCoefs.coefDuration != null ? avgCommissionCoefs.coefDuration.toFixed(2) : '-' }}</div>
            </div>
          </Tooltip>
        </div>
        <!-- Coef. contrat -->
        <div v-if="currentVariable && !isSpecialPayplan" class="flex-1 min-w-0">
          <Tooltip tip="Moyenne du coefficient contrat sur vos commissions">
            <div class="bg-slate-50 rounded-lg p-5 border-2 border-slate-200 h-full cursor-help">
              <div class="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-3">Contrat</div>
              <div class="text-2xl font-extrabold text-slate-900">{{ avgCommissionCoefs.coefContract != null ? avgCommissionCoefs.coefContract.toFixed(2) : '-' }}</div>
            </div>
          </Tooltip>
        </div>
        <!-- Coef. paiement -->
        <div v-if="currentVariable && !isSpecialPayplan" class="flex-1 min-w-0">
          <Tooltip tip="Moyenne du coefficient paiement sur vos commissions">
            <div class="bg-slate-50 rounded-lg p-5 border-2 border-slate-200 h-full cursor-help">
              <div class="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-3">Paiement</div>
              <div class="text-2xl font-extrabold text-slate-900">{{ avgCommissionCoefs.coefPayment != null ? avgCommissionCoefs.coefPayment.toFixed(2) : '-' }}</div>
            </div>
          </Tooltip>
        </div>
        <!-- Coef. IAV -->
        <div v-if="currentVariable && !isSpecialPayplan" class="flex-1 min-w-0">
          <Tooltip tip="Moyenne du coefficient IAV sur vos commissions">
            <div class="bg-slate-50 rounded-lg p-5 border-2 border-slate-200 h-full cursor-help">
              <div class="text-xs uppercase tracking-wider font-semibold text-slate-700 mb-3">IAV</div>
              <div class="text-2xl font-extrabold text-slate-900">{{ avgCommissionCoefs.coefIAV != null ? avgCommissionCoefs.coefIAV.toFixed(2) : '-' }}</div>
            </div>
          </Tooltip>
        </div>
      </div>
      <p v-if="avgCommissionCoefs.count && !isSpecialPayplan" class="text-xs text-gray-500 mt-2">
        Moyennes sur {{ avgCommissionCoefs.count }} commission(s)
      </p>
    </div>
    <div v-if="isManager || currentVariable" class="bg-white rounded-xl shadow-sm p-8 border border-gray-200">
      <h2 v-if="isManager" class="text-2xl font-bold text-gray-900 mb-6">Variables Commerciaux</h2>
      <GridVue
        v-if="isManager"
        :list="variableFormated || []"
        :getRowStyle="
          (params: any) => {
            switch (params.data.variableStatus) {
              case 'INIT':
                return { backgroundColor: 'var(--color-orange-200)' }
              case 'VALIDATION_IC':
              case 'VALIDATION_MANAGER':
                return { backgroundColor: 'var(--color-blue-200)' }
              case 'SENT_TO_LUCCA':
                return { backgroundColor: 'var(--color-emerald-200)' }
              default:
                return { backgroundColor: 'var(--color-white)' }
            }
          }
        "
        :cols="[
          { field: 'salesEngineer', headerName: 'Commercial', cellClass: 'border-0', filter: false },
          {
              field: 'totalCounterMarge',
              headerName: 'Marge Compteur',
              cellClass: 'border-0',
              filter: false,
              valueFormatter: (p: any) =>
                p.value != null ? `${p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-',
            },
          {
            field: 'reachedRate',
            headerName: 'Taux Atteint',
            cellClass: 'border-0',
            filter: false,
            valueFormatter: (p: any) =>
              p.value != null ? `${(p.value * 100).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %` : '-',
          },
          { field: 'variable', headerName: 'Variable', cellClass: 'border-0', filter: false,  valueFormatter: (p: any) =>
          p.value != null ? `${p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-', },
          { field: 'recovery', headerName: 'Reprise', cellClass: 'border-0', filter: false,  valueFormatter: (p: any) =>
          p.value != null ? `${p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-', },
          { field: 'complement', headerName: 'Complément', cellClass: 'border-0', filter: false,  valueFormatter: (p: any) =>
          p.value != null ? `${p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-', },
          { field: 'amount', headerName: 'Total VAriable', cellClass: 'border-0', filter: false,  valueFormatter: (p: any) =>
          p.value != null ? `${p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-', },
          {
            field: 'action',
            headerName: 'Actions',
            resizable: false,
            filter: false,
            minWidth: 150,
            maxWidth: 150,
            cellRenderer: VariableGridActions,
            cellRendererParams: {
              openModalEdit: (params: any) => {
                openModalEdit(params)
              },
              validateVariable: (id: string) => validateVariable(id as any),
              tooltipPosition: 'left',
            },
            sortable: false,
            cellClass: 'border-0 grid-action',
          },
        ]"
      />

      <template v-if="currentVariable">
        <h2 class="text-2xl font-bold text-gray-900 mb-6 mt-8">Commandes</h2>
        <GridVue
          :list="commissionsFormated"
          :getRowStyle="() => ({ minHeight: '60px' })"
          :cols="[
            ...(isManager ? [{ field: 'salesEngineer', headerName: 'Commercial', cellClass: 'border-0', filter: false }] : []),
            { field: 'raisonSociale', headerName: 'Raison sociale', cellClass: 'border-0', filter: false },
            { field: 'reference', headerName: 'Référence', cellClass: 'border-0', filter: false },
            ...(!isSpecialPayplan
              ? [
                  {
                    field: 'typeContrat',
                    headerName: 'Type contrat',
                    cellClass: 'border-0 type-coef-cell',
                    filter: false,
                    cellRenderer: typeCoefRenderer,
                    cellRendererParams: { coefField: 'typeContratCoef' },
                  },
                  {
                    field: 'duree',
                    headerName: 'Durée',
                    cellClass: 'border-0 type-coef-cell',
                    filter: false,
                    cellRenderer: typeCoefRenderer,
                    cellRendererParams: { coefField: 'dureeCoef' },
                  },
                  {
                    field: 'facturation',
                    headerName: 'Facturation',
                    cellClass: 'border-0 type-coef-cell',
                    filter: false,
                    cellRenderer: typeCoefRenderer,
                    cellRendererParams: { coefField: 'facturationCoef' },
                  },
                  {
                    field: 'iav',
                    headerName: 'IAV',
                    cellClass: 'border-0 type-coef-cell',
                    filter: false,
                    cellRenderer: typeCoefRenderer,
                    cellRendererParams: { coefField: 'iavCoef' },
                  },
                  { field: 'isLead', headerName: 'Lead', cellClass: 'border-0', filter: false },
                ]
              : []),
            {
              field: 'margeCompteur',
              headerName: 'Marge compteur',
              cellClass: 'border-0',
              filter: false,
              valueFormatter: (p: any) =>
                p.value != null ? `${p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-',
            },
            ...(!isSpecialPayplan
              ? [
                  {
                    field: 'commission',
                    headerName: 'Commission',
                    cellClass: 'border-0',
                    filter: false,
                    valueFormatter: (p: any) =>
                      p.value != null ? `${p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-',
                  },
                ]
              : []),
            {
              field: 'orderUuid',
              headerName: '',
              cellClass: 'border-0',
              filter: false,
              sortable: false,
              resizable: false,
              minWidth: 80,
              maxWidth: 80,
              cellRenderer: orderLinkRenderer,
            },
          ]"
        />

        <template v-if="regulationsFormatted.length > 0">
          <h2 class="text-2xl font-bold text-gray-900 mb-6 mt-8">Régularisations</h2>
          <GridVue
            :list="regulationsFormatted"
            :cols="[
              ...(isManager ? [{ field: 'salesEngineer', headerName: 'Commercial', cellClass: 'border-0', filter: false }] : []),
              { field: 'raisonSociale', headerName: 'Raison sociale', cellClass: 'border-0', filter: false },
              { field: 'reference', headerName: 'Référence devis', cellClass: 'border-0', filter: false },
              { field: 'family', headerName: 'Famille', cellClass: 'border-0', filter: false },
              {
                field: 'amount',
                headerName: 'Montant',
                cellClass: 'border-0',
                filter: false,
                valueFormatter: (p: any) =>
                  p.value != null ? `${p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-',
              },
              {
                field: 'contractCoef',
                headerName: 'Coef contrat',
                cellClass: 'border-0',
                filter: false,
                valueFormatter: (p: any) => (p.value != null ? p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'),
              },
              {
                field: 'durationCoef',
                headerName: 'Coef durée',
                cellClass: 'border-0',
                filter: false,
                valueFormatter: (p: any) => (p.value != null ? p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'),
              },
              {
                field: 'paymentCoef',
                headerName: 'Coef paiement',
                cellClass: 'border-0',
                filter: false,
                valueFormatter: (p: any) => (p.value != null ? p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'),
              },
              {
                field: 'iavCoef',
                headerName: 'Coef IAV',
                cellClass: 'border-0',
                filter: false,
                valueFormatter: (p: any) => (p.value != null ? p.value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'),
              },
              { field: 'reason', headerName: 'Motif', cellClass: 'border-0', filter: false },
              { field: 'status', headerName: 'Statut', cellClass: 'border-0', filter: false },
              { field: 'author', headerName: 'Auteur', cellClass: 'border-0', filter: false },
              { field: 'validatedAt', headerName: 'Validée le', cellClass: 'border-0', filter: false },
            ]"
          />
        </template>
      </template>
    </div>
  </div>
  <VariableEdit :show-modal="showModalEdit" :variable="variableEditParams" :readonly-adjustments="!canEditAdjustments" @close="onVariableEditClose" />
  <ConfirmModal
    :show-modal="showConfirmValidate"
    title="Valider la variable"
    body="La variable va être envoyée à votre manager pour validation."
    confirm-text="Confirmer"
    cancel-text="Annuler"
    confirm-variant="warning"
    :loading="isValidating"
    @confirm="onConfirmValidateVariable"
    @cancel="showConfirmValidate = false"
  />
</template>

<style scoped></style>
